import type { Role } from '@shared/types/User';
import type { FeatureGate } from '@infra/licence/FeatureGate';
import { ROLE_CATALOG } from '@application/access/roleCatalogContract';
import { ALWAYS_VISIBLE_VIEWS, CABINET_ONLY_VIEWS, viewsForFeatureGate } from '@application/licence/ModuleCatalog';

/** Jeton de permission sidebar (charte v2.2). */
export type ShellPerm = 'admin' | 'rh' | 'compta' | 'lecture' | 'salarie';

export type RoleViewProfiles = Record<string, readonly string[]>;

/** Profils par défaut (alignés sur erp-role-profile-catalog côté licensing-api). */
export const DEFAULT_ROLE_VIEW_PROFILES: RoleViewProfiles = {
  salarie: ROLE_CATALOG.view_profiles.salarie ?? [],
  // Le contrat nomme ce rôle « comptable » ; le poste le désigne « compta » (jeton de permission).
  compta: ROLE_CATALOG.view_profiles.comptable ?? [],
  rh: ROLE_CATALOG.view_profiles.rh ?? [],
  admin: ['*'],
  lecture: ROLE_CATALOG.view_profiles.lecture ?? [],
};

/** Écrans système accessibles à l'admin même sans module métier associé. */
export const ADMIN_SYSTEM_VIEWS = new Set([
  'utilisateurs',
  'audit',
  'parametres',
  'modules',
  'confidentialite',
]);

/** Fallback statique si certificat sans role_views (aligné shell.html). */
export const VIEW_ROLE_PERMISSIONS: Record<string, readonly ShellPerm[] | 'all'> = {
  cabinet: ['admin', 'rh', 'compta', 'lecture'],
  portefeuille: ['admin', 'rh', 'compta', 'lecture'],
  dashboard: 'all',
  pilotage: ['admin', 'rh', 'compta'],
  entreprise: ['admin', 'rh'],
  salaries: ['admin', 'rh', 'compta'],
  saisie: ['admin', 'rh', 'compta'],
  bulletins: ['admin', 'rh', 'compta'],
  declarations: ['admin', 'rh', 'compta'],
  comptabilite: ['admin', 'rh', 'compta', 'lecture'],
  exercices: ['admin', 'rh', 'compta', 'lecture'],
  immobilisations: ['admin', 'rh', 'compta', 'lecture'],
  etats: ['admin', 'rh', 'compta', 'lecture'],
  analytique: ['admin', 'rh', 'compta', 'lecture'],
  tiers: ['admin', 'rh', 'compta', 'lecture'],
  achats: ['admin', 'rh', 'compta', 'lecture'],
  ventes: ['admin', 'rh', 'compta', 'lecture'],
  stocks: ['admin', 'rh', 'compta', 'lecture'],
  tva: ['admin', 'rh', 'compta', 'lecture'],
  conges: ['admin', 'rh', 'compta'],
  coffrefort: ['admin', 'rh'],
  attestations: ['admin', 'rh'],
  evaluations: ['admin', 'rh'],
  recrutement: ['admin', 'rh'],
  flotte: ['admin', 'rh'],
  frais: ['admin', 'rh', 'compta', 'lecture'],
  'echeances-fiscales': ['admin', 'rh', 'compta', 'lecture'],
  conformite: ['admin', 'rh', 'compta', 'lecture'],
  'vie-sociale': ['admin', 'rh', 'compta', 'lecture'],
  sanctions: ['admin', 'rh'],
  rgpd: ['admin', 'rh'],
  confidentialite: ['admin', 'rh'],
  qualite: ['admin', 'rh', 'compta', 'lecture'],
  'self-bulletins': ['salarie'],
  'self-conges': ['salarie'],
  'self-docs': ['salarie'],
  utilisateurs: ['admin'],
  audit: ['admin'],
  parametres: ['admin', 'rh'],
  modules: ['admin'],
  'nexxus-factures': ['admin', 'rh', 'compta'],
};

export const ROLE_TO_SHELL_PERM: Record<Role, ShellPerm> = {
  super_admin: 'admin',
  admin: 'admin',
  rh: 'rh',
  comptable: 'compta',
  lecture: 'lecture',
  salarie: 'salarie',
};

export const SHELL_PERM_LABELS: Record<ShellPerm, string> = {
  admin: 'Administrateur',
  rh: 'Gestionnaire RH',
  compta: 'Comptable',
  lecture: 'Lecture seule',
  salarie: 'Salarié',
};

export const VIEW_LABELS: Record<string, string> = {
  cabinet: 'Tableau de bord dossiers',
  portefeuille: 'Portefeuille clients',
  dashboard: 'Tableau de bord',
  pilotage: 'Pilotage RH',
  entreprise: 'Mon entreprise',
  salaries: 'Salariés',
  saisie: 'Saisie mensuelle',
  bulletins: 'Bulletins',
  declarations: 'Déclarations sociales',
  comptabilite: 'Comptabilité',
  exercices: 'Exercices fiscaux',
  immobilisations: 'Immobilisations',
  etats: 'États financiers',
  analytique: 'Comptabilité analytique',
  tiers: 'Tiers',
  achats: 'Achats',
  ventes: 'Ventes',
  stocks: 'Stocks',
  tva: 'TVA',
  conges: 'Congés & absences',
  coffrefort: 'Coffre-fort RH',
  attestations: 'Attestations',
  evaluations: 'Évaluations',
  recrutement: 'Recrutement',
  flotte: 'Flotte de véhicules',
  frais: 'Notes de frais',
  'echeances-fiscales': 'Calendrier fiscal',
  conformite: 'Conformité OHADA & Gabon',
  'vie-sociale': 'Vie sociale & gouvernance',
  sanctions: 'Sanctions & suspensions',
  rgpd: 'Registre RGPD / DPO',
  confidentialite: 'Confidentialité & Données',
  qualite: 'Centre Qualité',
  'self-bulletins': 'Mes bulletins',
  'self-conges': 'Mes congés',
  'self-docs': 'Mes documents',
  utilisateurs: 'Utilisateurs',
  audit: "Journal d'audit",
  parametres: 'Paramètres',
  modules: 'Modules supplémentaires',
  'nexxus-factures': 'Factures Nexxus',
};

let activeRoleProfiles: RoleViewProfiles | null = null;

/** Charge les profils signés dans le certificat de licence. */
export function setActiveRoleProfiles(profiles: Record<string, string[]> | undefined | null): void {
  if (!profiles) {
    activeRoleProfiles = null;
    return;
  }
  const mapped: RoleViewProfiles = {};
  for (const [role, views] of Object.entries(profiles)) {
    const shellRole = role === 'comptable' ? 'compta' : role;
    mapped[shellRole] = views;
  }
  activeRoleProfiles = mapped;
}

export function getActiveRoleProfiles(): RoleViewProfiles {
  const merged: RoleViewProfiles = { ...DEFAULT_ROLE_VIEW_PROFILES };
  if (activeRoleProfiles) {
    for (const [role, views] of Object.entries(activeRoleProfiles)) {
      const base = merged[role];
      if (!base) {
        merged[role] = views;
        continue;
      }
      if (base.includes('*')) {
        merged[role] = views.includes('*') ? ['*'] : views;
        continue;
      }
      const allowed = new Set(base);
      merged[role] = views.filter((view) => allowed.has(view));
    }
  }

  const salarieMin = DEFAULT_ROLE_VIEW_PROFILES.salarie ?? [];
  const currentSalarie = merged.salarie ?? [];
  if (!currentSalarie.includes('*')) {
    merged.salarie = [...new Set([...salarieMin, ...currentSalarie])];
  }

  const adminViews = merged.admin;
  if (adminViews && !adminViews.includes('*')) {
    const missingSystem = [...ADMIN_SYSTEM_VIEWS].filter((view) => !adminViews.includes(view));
    if (missingSystem.length > 0) {
      merged.admin = [...adminViews, ...missingSystem];
    }
  }
  for (const [role, views] of Object.entries(merged)) {
    if (views.includes('*')) continue;
    const required = ['dashboard'];
    if (['admin', 'rh', 'compta'].includes(role)) required.push('nexxus-factures');
    if (role === 'admin') required.push('modules');
    if (role === 'admin' || role === 'rh') required.push('confidentialite');
    const missing = required.filter((view) => !views.includes(view));
    if (missing.length) merged[role] = [...missing, ...views];
  }
  return merged;
}

export function roleToShellPerm(role: Role): ShellPerm {
  return ROLE_TO_SHELL_PERM[role];
}

function viewsForPerm(perm: ShellPerm): readonly string[] | '*' {
  const profiles = getActiveRoleProfiles();
  const views = profiles[perm];
  if (!views) return [];
  if (views.includes('*')) return '*';
  return views;
}

export function isViewAllowedForRole(view: string, perm: ShellPerm): boolean {
  const allowed = viewsForPerm(perm);
  if (allowed === '*') {
    return true;
  }
  if (allowed.length > 0) {
    return allowed.includes(view);
  }
  const access = VIEW_ROLE_PERMISSIONS[view];
  if (!access) return false;
  if (access === 'all') return true;
  return access.includes(perm);
}

export function licensedViewsForGate(gate: FeatureGate | null | undefined): Set<string> {
  if (!gate) return new Set(ALWAYS_VISIBLE_VIEWS);
  return viewsForFeatureGate(gate);
}

/** Création, modification et sortie : admin et RH. Le comptable consulte. */
export const MUTATION_ROLES: readonly ShellPerm[] = ['admin', 'rh'];

export function canMutateRecords(perm: ShellPerm): boolean {
  return MUTATION_ROLES.includes(perm);
}

/** Saisie comptable et factures : admin, RH et comptable. */
export const COMPTA_MUTATION_ROLES: readonly ShellPerm[] = ['admin', 'rh', 'compta'];

export function canMutateComptaRecords(perm: ShellPerm): boolean {
  return COMPTA_MUTATION_ROLES.includes(perm);
}

/**
 * Écrans où le comptable écrit (même règle en local et en remote).
 * Déclarations DTS, congés, RH, gouvernance : consultation seulement.
 */
export const COMPTA_WRITE_VIEWS = new Set([
  'saisie',
  'comptabilite',
  'exercices',
  'analytique',
  'tiers',
  'achats',
  'ventes',
  'stocks',
  'tva',
  'conformite',
  'immobilisations',
]);

export function canMutateView(view: string, perm: ShellPerm): boolean {
  if (COMPTA_WRITE_VIEWS.has(view)) return canMutateComptaRecords(perm);
  return canMutateRecords(perm);
}

export function isViewAllowedForAccess(
  view: string,
  perm: ShellPerm,
  gate: FeatureGate | null | undefined,
  opts?: { cabinetMode?: boolean },
): boolean {
  if (CABINET_ONLY_VIEWS.has(view)) {
    return Boolean(opts?.cabinetMode) && isViewAllowedForRole(view, perm);
  }

  if (perm === 'admin' && ADMIN_SYSTEM_VIEWS.has(view)) {
    return true;
  }

  const licensed = licensedViewsForGate(gate);
  if (!licensed.has(view)) return false;
  return isViewAllowedForRole(view, perm);
}

/** Libellés d'écrans accessibles pour un profil (référentiel utilisateurs). */
export function accessibleViewLabelsForPerm(
  perm: ShellPerm,
  gate: FeatureGate | null | undefined,
  opts?: { cabinetMode?: boolean },
): string[] {
  return Object.keys(VIEW_LABELS)
    .filter((view) => isViewAllowedForAccess(view, perm, gate, opts))
    .map((view) => VIEW_LABELS[view] ?? view)
    .sort((a, b) => a.localeCompare(b, 'fr'));
}
